import { useMemo } from "react";

import { Install } from "@similarweb/icons";
import { ChipOverflowCell } from "@similarweb/pro-logic-components";
import type { IChipLabel } from "@similarweb/pro-logic-components/dist/table/cells/components/views/chip-overflow-cell/ChipOverflowCell";
import { Tooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";

import { RankedListTooltipContent } from "./RankedListTooltipContent";
import type { BrandWithShare, IRankedListTooltipRow } from "./types";

interface ITopBrandsCellProps {
    topBrands: BrandWithShare[];
    coreCompetitors?: string[];
}

export const TopBrandsCell = ({ topBrands, coreCompetitors }: ITopBrandsCellProps) => {
    const tooltipRows = useMemo<IRankedListTooltipRow[]>(
        () =>
            [...topBrands]
                .sort((a, b) => {
                    const diff = (b.visibilityShare ?? 0) - (a.visibilityShare ?? 0);
                    if (diff !== 0) {
                        return diff;
                    }

                    return (a.name ?? "").localeCompare(b.name ?? "");
                })
                .map((brand) => ({
                    leftValue: brand.name,
                    rightValue: percentageSignFilter()(brand.visibilityShare ?? 0, 0) as string,
                })),
        [topBrands],
    );

    const labels = useMemo<IChipLabel[]>(
        () =>
            topBrands.map((brand) => ({
                text: brand.name ?? "",
                icon: coreCompetitors?.some((competitor) => competitor.toLowerCase() === brand.name.toLowerCase()) ? (
                    <Install />
                ) : undefined,
            })),
        [topBrands, coreCompetitors],
    );

    return topBrands.length ? (
        <Tooltip
            placement="top"
            content={
                <RankedListTooltipContent
                    automationName="prompts-top-brands-tooltip"
                    leftHeader={translate("llm.prompts.column.top_brands.tooltip.header.brand")}
                    rightHeader={translate("llm.prompts.column.top_brands.tooltip.header.visibility")}
                    rows={tooltipRows}
                />
            }
        >
            <Box data-automation="prompts-top-brands-cell">
                <ChipOverflowCell
                    labels={labels}
                    disableHiddenLabelsTooltip
                />
            </Box>
        </Tooltip>
    ) : (
        NO_DATA_SIGN
    );
};
